import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "components/layout/MainLayout";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Input,
  Textarea,
  Button,
  Checkbox,
  ColorPicker,
} from "components/ui";
import { contactUsService } from "services/contactus.service";
import { useToast } from "hooks/useToast";
import { ROUTES } from "constants/index";
import { useFieldValidation } from "hooks/useFieldValidation";
import { LogoIntelligenceResult } from "@/utils/logoIntelligence";

type ContactUsFormState = {
  name: string;
  companyName: string;
  email: string;
  message: string;
  date?: string;
};

export const ContactUsFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isView = location.pathname.endsWith("/view");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const [form, setForm] = useState<ContactUsFormState>({
    name: "",
    email: "",
    message: "",
    date: "",
    companyName: "",
  });

  const [logoInsights, setLogoInsights] = useState<
    LogoIntelligenceResult | undefined
  >();

  useEffect(() => {
    const load = async () => {
      if ((isView) && id) {
        setIsLoading(true);
        setError(null);
        try {
          const mail = await contactUsService.getById(id);
          setForm({
            name: mail.name || "",
            companyName: mail.companyName ?? "",
            message: mail.message || "",
            email: mail.email || "",
            date: mail.date ?? "",
          });
        } catch (e: any) {
          const friendly =
            "An error occurred on the server. Please contact the administrator.";
          setError(e.message ?? friendly);
          toast.error(e.message ?? friendly);
        } finally {
          setIsLoading(false);
        }
      }
    };
    load();
  }, [id, isView]);


  const handleColorChange = (field: "color") => (color: string) => {
    setForm((prev) => ({ ...prev, [field]: color }));
  };

  const palettePresets = useMemo(() => {
    const defaults = ["#ffffff", "#C0C0C0", "#f44336", "#FFD700", "#E5E4E2"];
    const aiColors = (logoInsights?.palette ?? [])
      .map((color) => color?.trim().toLowerCase())
      .filter((color): color is string => Boolean(color));

    const unique = Array.from(new Set([...aiColors, ...defaults]));
    return unique.slice(0, 10);
  }, [logoInsights]);

  const handleChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: checked, // <-- Checkbox value is boolean
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;

    // For numeric fields, prevent non-numeric keys
    if (name === "validityMonths" || name === "numberOfUsers") {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
        return;
      }
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      if (
        (e.ctrlKey || e.metaKey) &&
        [65, 67, 86, 88, 90].includes(e.keyCode)
      ) {
        return;
      }
      // Prevent if not a number
      if (
        (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
        (e.keyCode < 96 || e.keyCode > 105)
      ) {
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;

    // For numeric fields, filter pasted content
    if (name === "validityMonths" || name === "numberOfUsers") {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const numericValue = pastedText.replace(/[^0-9]/g, "");
      setForm((prev) => {
        const fieldName = name as keyof ContactUsFormState;
        const next = { ...prev, [fieldName]: numericValue };
        return next;
      });
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader
          title={"View Message"}
        />
        <CardBody>
          <Grid cols={2} gap="lg">
            <GridItem>
              <Input
                label="Name"
                name="name"
                value={form.name}
                fullWidth
                disabled={isView}
              />
            </GridItem>
            <GridItem>
              <Input
                label="Company name"
                name="companyName"
                value={form.companyName}
                fullWidth
                disabled={isView}
              />
            </GridItem>
            <GridItem>
              <Input
                label="Email"
                name="email"
                value={form.email}
                fullWidth
                disabled={isView}
              />
            </GridItem>
            <GridItem>
              <Input
                label="Date"
                name="date"
                value={form.date}
                fullWidth
                disabled={isView}
              />
            </GridItem>
            <GridItem colSpan={2}>
              <Textarea
                label="Message"
                name="message"
                value={form.message}
                fullWidth
                disabled={isView}
              />
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      <div className="flex justify-end gap-3 mt-6">
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.CONTACTUS)}
          disabled={isLoading}
        >
          Back
        </Button>
      </div>
    </MainLayout>
  );
};
